import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe, Copy, Check, Code } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/card';
import { Tooltip } from '@/components/ui/tooltip';
import type { ApiResponse } from '@/types/api';

/**
 * Website Chat embed. Shows the merchant a <script> snippet to drop on their own
 * site. Visitors chat via the widget and get AI replies (products + Business
 * Knowledge RAG); conversations land in the same inbox (platform: webchat).
 */
export function WebChatEmbedCard() {
  const [copied, setCopied] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['webchat', 'embed'],
    queryFn: () =>
      apiClient.get<ApiResponse<{ snippet: string; scriptUrl: string; key: string }>>('/api/webchat/my-embed')
        .then((r) => r.data.data),
    staleTime: 5 * 60 * 1000,
  });

  const snippet = data?.snippet ?? '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Embed snippet copied');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs">
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Globe className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Website Chat Widget Embed
          </span>
          <Tooltip content="HTML JavaScript snippet ready to embed on any storefront" placement="top">
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200/80 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 px-2 py-0.2 text-[9px] font-bold text-sky-700 dark:text-sky-300 cursor-pointer">
              <Code className="h-2.5 w-2.5" />
              Script Snippet
            </span>
          </Tooltip>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
          Paste before &lt;/body&gt; tag on your site
        </span>
      </div>

      <div className="p-3 bg-white/70 dark:bg-slate-900/70">
        {isLoading ? (
          <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
        ) : (
          <div className="relative rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-900 dark:bg-slate-950 p-3 shadow-inner">
            <pre className="overflow-x-auto pr-16 text-xs text-slate-200 font-mono leading-relaxed">
              <code>{snippet || '<!-- Loading webchat snippet... -->'}</code>
            </pre>
            <button
              type="button"
              onClick={copy}
              aria-label="Copy embed snippet"
              className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-800/90 px-2.5 py-1 text-xs font-semibold text-slate-200 transition-colors hover:bg-slate-700 hover:text-white"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

export default WebChatEmbedCard;
